<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import GoogleCalendarAPI from '../../../../api/googleCalendar';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  showControls: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['updated', 'error']);

const { t, locale } = useI18n();

const dateLocale = computed(() =>
  (locale.value || 'pt_BR').startsWith('pt') ? ptBR : enUS
);
const { showAlert } = useAlert();

// State
// Guardamos QUAL ação está em voo, não apenas "tem algo em voo": o spinner
// precisa aparecer no botão que o usuário clicou. Com um booleano único, pedir
// "remover do Google" acendia o spinner do botão "sincronizar agora".
const pendingAction = ref(null);
const syncError = ref(null);

const isSyncing = computed(() => pendingAction.value !== null);

// Computed properties
const syncEnabled = computed(
  () => props.item.item_details?.google_sync_enabled || false
);

const hasGoogleEvent = computed(
  // Auditoria 2026-08-08 (G2): o vínculo é a coluna `google_event_id` do card.
  // Aqui se procurava `item_details.google_calendar_event_id`, chave que nenhum
  // caminho de escrita cria — o componente nunca reconhecia um card já
  // sincronizado. O fallback cobre payloads antigos que ainda tragam a chave.
  () =>
    !!(
      props.item.google_event_id ||
      props.item.item_details?.google_calendar_event_id
    )
);

const hasSchedulingData = computed(
  () =>
    !!(
      props.item.item_details?.scheduled_at ||
      props.item.item_details?.deadline_at
    )
);

const lastSyncedAt = computed(() => props.item.item_details?.last_synced_at);

// Um único descritor por estado. Antes eram três cascatas paralelas de quatro
// ramos (classe, ícone e texto) e a de classe pintava "sincronizando",
// "sincronizado" e "aguardando" com exatamente as mesmas cores — o badge
// mudava de texto, nunca de cor. Os nomes de ícone também não existiam no
// conjunto Fluent (`calendar-check`, `delete-bin`, `refresh`…), então cada um
// renderizava um <svg> vazio e um console.warn.
// As chaves ficam literais em cada ramo de propósito: chave montada em
// variável some das ferramentas de extração/paridade (e o lint acusa).
// Traduzir dentro do computed é seguro — ele reavalia quando o idioma muda.
const syncStatus = computed(() => {
  if (isSyncing.value) {
    return {
      badgeClass: 'bg-n-alpha-2 text-n-slate-11',
      icon: 'i-lucide-loader-2',
      label: t('GOOGLE_CALENDAR.SYNCING'),
    };
  }

  if (syncError.value) {
    return {
      badgeClass: 'bg-n-ruby-3 text-n-ruby-11',
      icon: 'i-lucide-alert-circle',
      label: t('GOOGLE_CALENDAR.SYNC_ERROR'),
    };
  }

  if (hasGoogleEvent.value) {
    return {
      badgeClass: 'bg-n-teal-3 text-n-teal-11',
      icon: 'i-lucide-calendar-check',
      label: t('GOOGLE_CALENDAR.SYNCED'),
    };
  }

  return {
    badgeClass: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-calendar',
    label: t('GOOGLE_CALENDAR.SYNC_PENDING'),
  };
});

const formatSyncTime = computed(() => {
  if (!lastSyncedAt.value) return '';

  try {
    const date = new Date(lastSyncedAt.value);
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: dateLocale.value,
    });
  } catch (error) {
    return lastSyncedAt.value;
  }
});

// Methods
const toggleSync = async () => {
  if (isSyncing.value) return;

  try {
    pendingAction.value = 'toggle';
    syncError.value = null;

    const newState = !syncEnabled.value;

    const response = await GoogleCalendarAPI.toggleSync(
      props.item.id,
      newState,
      false // Don't remove from Google when disabling
    );

    if (response.data.success) {
      emit('updated', response.data.pipeline_card);
      showAlert(response.data.message);
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    syncError.value = error.message || t('GOOGLE_CALENDAR.SYNC_TOGGLE_ERROR');
    emit('error', error);
    showAlert(t('GOOGLE_CALENDAR.SYNC_TOGGLE_ERROR'));
  } finally {
    pendingAction.value = null;
  }
};

const manualSync = async () => {
  if (isSyncing.value) return;

  try {
    pendingAction.value = 'sync';
    syncError.value = null;

    const response = await GoogleCalendarAPI.syncToGoogle(props.item.id);

    if (response.data.success) {
      emit('updated', response.data.pipeline_card);
      showAlert(response.data.message);
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    syncError.value = error.message || t('GOOGLE_CALENDAR.MANUAL_SYNC_ERROR');
    emit('error', error);
    showAlert(t('GOOGLE_CALENDAR.MANUAL_SYNC_ERROR'));
  } finally {
    pendingAction.value = null;
  }
};

const removeFromGoogle = async () => {
  if (isSyncing.value) return;

  try {
    pendingAction.value = 'remove';
    syncError.value = null;

    const response = await GoogleCalendarAPI.removeFromGoogle(props.item.id);

    if (response.data.success) {
      emit('updated', response.data.pipeline_card);
      showAlert(response.data.message);
    } else {
      throw new Error(response.data.error);
    }
  } catch (error) {
    syncError.value = error.message || t('GOOGLE_CALENDAR.REMOVE_ERROR');
    emit('error', error);
    showAlert(t('GOOGLE_CALENDAR.REMOVE_ERROR'));
  } finally {
    pendingAction.value = null;
  }
};

// Watch for prop changes
watch(
  () => props.item,
  () => {
    syncError.value = null;
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Sync Status Badge -->
    <div
      v-if="syncEnabled"
      class="inline-flex items-center self-start gap-1 px-2 py-1 text-xs font-medium rounded-full"
      :class="syncStatus.badgeClass"
      role="status"
    >
      <span
        class="flex-shrink-0 w-3.5 h-3.5"
        :class="[syncStatus.icon, { 'animate-spin': isSyncing }]"
        aria-hidden="true"
      />
      <span>{{ syncStatus.label }}</span>
    </div>

    <!-- Sync Controls -->
    <div v-if="showControls" class="flex flex-col gap-2">
      <div class="flex flex-wrap items-center gap-4">
        <!-- Toggle Sync -->
        <Button
          variant="link"
          color="slate"
          size="xs"
          class="min-h-11"
          :icon="syncEnabled ? 'i-lucide-calendar-x' : 'i-lucide-calendar-plus'"
          :label="
            syncEnabled
              ? t('GOOGLE_CALENDAR.DISABLE_SYNC')
              : t('GOOGLE_CALENDAR.ENABLE_SYNC')
          "
          :is-loading="pendingAction === 'toggle'"
          :disabled="isSyncing"
          @click="toggleSync"
        />

        <!-- Manual Sync -->
        <Button
          v-if="syncEnabled && hasSchedulingData"
          variant="link"
          color="blue"
          size="xs"
          class="min-h-11"
          icon="i-lucide-refresh-cw"
          :label="t('GOOGLE_CALENDAR.SYNC_NOW')"
          :is-loading="pendingAction === 'sync'"
          :disabled="isSyncing"
          @click="manualSync"
        />

        <!-- Remove from Google -->
        <Button
          v-if="syncEnabled && hasGoogleEvent"
          variant="link"
          color="ruby"
          size="xs"
          class="min-h-11"
          icon="i-lucide-trash-2"
          :label="t('GOOGLE_CALENDAR.REMOVE_FROM_GOOGLE')"
          :is-loading="pendingAction === 'remove'"
          :disabled="isSyncing"
          @click="removeFromGoogle"
        />
      </div>

      <!-- Sync Info -->
      <!-- Rótulo e valor em elementos separados: o ": " literal que existia
           aqui era texto cru fora do i18n (e o lint acusava). O gap do flex
           faz a separação sem inventar chave nova. -->
      <div
        v-if="lastSyncedAt"
        class="flex flex-wrap items-center gap-1 text-xs text-n-slate-11"
      >
        <span>{{ t('GOOGLE_CALENDAR.LAST_SYNCED') }}</span>
        <span>{{ formatSyncTime }}</span>
      </div>
    </div>

    <!-- Error Display -->
    <div
      v-if="syncError"
      class="flex items-center gap-1 text-xs text-n-ruby-11"
      role="alert"
    >
      <span
        class="flex-shrink-0 w-3.5 h-3.5 i-lucide-alert-circle"
        aria-hidden="true"
      />
      <span>{{ syncError }}</span>
    </div>
  </div>
</template>
